/**
 * Tasks Tab
 *
 * Displays tasks related to a specific record (Client, Contact, etc.)
 * Provides task creation with pre-populated related record.
 */

import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { persistedTaskRecords } from '../../services/mockData/tasks';
import { TaskModal } from '../modals/TaskModal';
import { CheckSquare, Plus, Calendar, User, AlertCircle } from 'lucide-react';
import type { ListViewRecord } from '../../types/listView';

interface TasksTabProps {
  /** The ID of the related record (client, contact, etc.) */
  relatedToId: string;
  /** The name of the related record for display */
  relatedToName: string;
  /** The type of related record */
  relatedToType?: 'Client' | 'Contact' | 'Account' | 'Case';
  /** Optional callback when navigating to a task */
  onNavigateToTask?: (taskId: string) => void;
}

export const TasksTab: React.FC<TasksTabProps> = ({
  relatedToId,
  relatedToName,
  relatedToType = 'Client',
  onNavigateToTask
}) => {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<ListViewRecord | null>(null);

  // Fetch all tasks and filter by related record
  const { data: allTasks = [], isLoading } = useQuery({
    queryKey: ['taskRecords'],
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 200));
      return persistedTaskRecords.get();
    }
  });

  // Filter tasks related to this record
  const relatedTasks = useMemo(() => {
    return allTasks.filter(task =>
      task.clientId === relatedToId ||
      task.relatedTo === relatedToId ||
      task.whatId === relatedToId ||
      task.accountId === relatedToId
    );
  }, [allTasks, relatedToId]);

  // Sort by due date (closest first), then by priority
  const sortedTasks = useMemo(() => {
    const priorityOrder: Record<string, number> = {
      'High': 0,
      'Medium': 1,
      'Low': 2
    };

    return [...relatedTasks].sort((a, b) => {
      // Sort by status first (Open/In Progress before Completed)
      if (a.status === 'Completed' && b.status !== 'Completed') return 1;
      if (a.status !== 'Completed' && b.status === 'Completed') return -1;

      // Then by due date
      const dateA = a.dueDate ? new Date(a.dueDate as string).getTime() : Infinity;
      const dateB = b.dueDate ? new Date(b.dueDate as string).getTime() : Infinity;
      if (dateA !== dateB) return dateA - dateB;

      // Then by priority
      const prioA = priorityOrder[a.priority as string] ?? 1;
      const prioB = priorityOrder[b.priority as string] ?? 1;
      return prioA - prioB;
    });
  }, [relatedTasks]);

  const handleNewTask = () => {
    setSelectedTask(null);
    setIsModalOpen(true);
  };

  const handleEditTask = (task: ListViewRecord) => {
    setSelectedTask(task);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTask(null);
    // Refresh task list
    queryClient.invalidateQueries({ queryKey: ['taskRecords'] });
  };

  // Status badge colors
  const statusColors: Record<string, string> = {
    'Open': 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    'In Progress': 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300',
    'Completed': 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    'Deferred': 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300'
  };

  // Priority badge colors
  const priorityColors: Record<string, string> = {
    'High': 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
    'Medium': 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300',
    'Low': 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300'
  };

  // Check if date is past due
  const isPastDue = (dueDate: string | undefined) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  // Format date for display
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="h-6 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          <div className="h-9 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-20 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Tasks ({sortedTasks.length})
        </h3>
        <button
          onClick={handleNewTask}
          className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Task
        </button>
      </div>

      {sortedTasks.length === 0 ? (
        <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-12">
          <div className="text-center">
            <CheckSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No tasks yet
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Create tasks to track action items and follow-ups for {relatedToName}.
            </p>
            <button
              onClick={handleNewTask}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create First Task
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {sortedTasks.map(task => (
            <div
              key={task.id}
              onClick={() => handleEditTask(task)}
              className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:border-blue-500 dark:hover:border-blue-500 cursor-pointer transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className={`font-medium truncate ${
                      task.status === 'Completed'
                        ? 'text-gray-500 dark:text-gray-400 line-through'
                        : 'text-gray-900 dark:text-white'
                    }`}>
                      {task.title as string}
                    </h4>
                    {task.status !== 'Completed' && isPastDue(task.dueDate as string) && (
                      <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    )}
                  </div>

                  <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      <span className={isPastDue(task.dueDate as string) && task.status !== 'Completed' ? 'text-red-500' : ''}>
                        {formatDate(task.dueDate as string)}
                      </span>
                    </span>
                    {task.assignee && (
                      <span className="flex items-center gap-1">
                        <User className="w-3.5 h-3.5" />
                        {task.assignee as string}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    priorityColors[task.priority as string] || priorityColors.Medium
                  }`}>
                    {task.priority as string}
                  </span>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    statusColors[task.status as string] || statusColors.Open
                  }`}>
                    {task.status as string}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Task Modal */}
      <TaskModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        task={selectedTask ? {
          ...selectedTask,
          // Pre-populate client info for new tasks
          clientId: selectedTask.clientId || relatedToId,
          clientName: selectedTask.clientName || relatedToName
        } : {
          // New task with pre-populated client
          id: '',
          clientId: relatedToId,
          clientName: relatedToName
        } as ListViewRecord}
      />
    </div>
  );
};
